import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { Dictionary, DictionaryDto } from '@app/shared/services/dtos/dictionary.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { finalize, Observable, shareReplay, tap } from 'rxjs';
import { LookupFactory } from './factories/lookup.factory';

@Injectable({ providedIn: 'root' })
export class LookupService extends DataService<DictionaryDto, Dictionary> {
  private readonly lookupFactory: LookupFactory = inject(LookupFactory);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);

  private dictionaries: DataStore<Dictionary> = new DataStore<DictionaryDto>();
  private dictionary: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public getDictionary(dictionaryType: string): Signal<Dictionary[] | undefined> {
    if (!dictionaryType) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.dictionary
          .get(dictionaryType)()
          ?.ids?.map(id => this.dictionaries.get(id)()) || []
    );
  }

  public getById(analysisId: string): Signal<Dictionary | undefined> {
    return computed(() => this.dictionaries.get(analysisId)());
  }

  public loadDictionary(dictionaryType: string): void {
    if (this.dictionary.hasDataForId(dictionaryType)) {
      return;
    }
    this.fetchDictionary(dictionaryType);
  }

  public upsertLocalData(dto: DictionaryDto): Signal<DictionaryDto> {
    return dto ? this.dictionaries.upsert(this.lookupFactory.produce(dto)) : signal(undefined);
  }

  private fetchDictionary(dictionaryType: string): void {
    this.loaderService.startLoading();

    this.httpClient.get<DictionaryDto[]>(environment.APIEndPoint + `lookups/${dictionaryType}`).subscribe({
      next: (dictionary: DictionaryDto[]) => {
        this.dictionaries.upsertMany(dictionary.map(itm => this.lookupFactory.produce(itm)));
        this.dictionary.upsert({
          id: dictionaryType,
          ids: dictionary.map(itm => itm.id)
        });
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  /**
   * Creates a dictionary entry and adds it to the local store. The request starts immediately (callers may ignore
   * the result); the returned observable replays the created entry, e.g. to select it once it has an id.
   */
  public addItemToDictionary(dictionaryType: string, item: Dictionary): Observable<DictionaryDto> {
    this.loaderService.startLoading();

    const request$ = this.httpClient
      .post<DictionaryDto>(environment.APIEndPoint + `lookups/${dictionaryType}`, item)
      .pipe(
        tap((dictionary: DictionaryDto) => {
          this.upsertLocalData(dictionary);

          const existingProjectOshAnalyses: string[] = this.dictionary.get(dictionaryType)().ids ?? [];
          this.dictionary.upsert({
            id: dictionaryType,
            ids: existingProjectOshAnalyses.concat(dictionary.id)
          });
        }),
        finalize(() => this.loaderService.stopLoading()),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    request$.subscribe({ error: () => undefined });

    return request$;
  }
}
